import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { deleteBanner, getBanners } from ".";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AdminPageLayout,
  AdminSectionCard,
  AdminAction,
  DeleteConfirmationDialog,
} from "../../components/admin";
import { resolveMediaUrl } from "../../utils/mediaUrl";

function getApiErrorMessage(error, fallback) {
  return (
    error.response?.data?.message ||
    error.response?.data?.data?.message ||
    error.message ||
    fallback
  );
}

function BannerCardImage({ imageUrl, name }) {
  const [src, setSrc] = useState(resolveMediaUrl(imageUrl));
  const [failed, setFailed] = useState(!imageUrl);

  useEffect(() => {
    setSrc(resolveMediaUrl(imageUrl));
    setFailed(!imageUrl);
  }, [imageUrl]);

  if (failed || !src) {
    return (
      <div className="aspect-video bg-stone-100 flex flex-col items-center justify-center text-stone-400">
        <svg className="h-10 w-10 mb-1" stroke="currentColor" fill="none" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="text-xs">Image unavailable</span>
      </div>
    );
  }

  return (
    <div className="aspect-video overflow-hidden bg-stone-100">
      <img
        src={src}
        alt={name || "Banner"}
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

export default function BannerListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const { results, updated } = location.state || {};
    if (results) {
      toast.success("Banner created successfully!", {
        toastId: "banner-create-success",
        position: "bottom-right",
        autoClose: 2000,
      });
      window.history.replaceState({}, document.title);
    } else if (updated) {
      toast.success("Banner updated successfully!", {
        toastId: "banner-update-success",
        position: "bottom-right",
        autoClose: 2000,
      });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getBanners();
      setBanners(response.data?.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to load banners"), {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
      });
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteBanner(deleteTarget.id);
      setBanners((prev) => prev.filter((banner) => banner.id !== deleteTarget.id));
      toast.success("Banner deleted successfully!", {
        toastId: "banner-delete-success",
        position: "bottom-right",
        autoClose: 2000,
      });
      setDeleteTarget(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to delete banner"), {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminPageLayout
      eyebrow="Homepage Promotion"
      title="Banners"
      description="Manage visual placements used across the platform homepage with a cleaner premium card system."
      actions={
        <AdminAction onClick={() => navigate("/banners/create")}>Create Banner</AdminAction>
      }
    >
      {loading ? (
        <AdminSectionCard title="Banner gallery" description="Loading banners…">
          <div className="admin-empty-state">Loading banners…</div>
        </AdminSectionCard>
      ) : banners.length === 0 ? (
        <AdminSectionCard title="Banner gallery" description="No banners are available yet.">
          <div className="admin-empty-state">No banners found. Create your first banner.</div>
        </AdminSectionCard>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {banners.map((banner) => {
            const displayName = banner.name || banner.title || "Untitled banner";
            return (
              <div key={banner.id} className="admin-surface overflow-hidden">
                <BannerCardImage imageUrl={banner.imageUrl} name={displayName} />
                <div className="p-4">
                  <h3 className="text-base font-semibold text-zinc-900 truncate" title={displayName}>
                    {displayName}
                  </h3>
                  {banner.redirectionUrl ? (
                    <p className="mt-1 text-xs text-stone-500 truncate" title={banner.redirectionUrl}>
                      {banner.redirectionUrl}
                    </p>
                  ) : null}
                  <div className="mt-3 flex gap-2">
                    <AdminAction
                      variant="outline"
                      fullWidth
                      onClick={() => navigate(`/banners/${banner.id}`)}
                    >
                      Edit
                    </AdminAction>
                    <AdminAction
                      variant="danger"
                      fullWidth
                      onClick={() => setDeleteTarget(banner)}
                    >
                      Delete
                    </AdminAction>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        itemName={deleteTarget?.name || deleteTarget?.title}
        loading={deleting}
      />
    </AdminPageLayout>
  );
}
